import { BrowserRouter, Link, Navigate, NavLink, Outlet, Route, Routes, useLocation } from "react-router";
import { AuditPage } from "./admin/AuditPage";
import { ExportsPage } from "./admin/ExportsPage";
import { PeoplePage } from "./admin/PeoplePage";
import { PersonPage } from "./admin/PersonPage";
import { SchedulesPage } from "./admin/SchedulesPage";
import { AdminSettingsPage } from "./admin/SettingsPage";
import { SitePage } from "./admin/SitePage";
import { SitesPage } from "./admin/SitesPage";
import { StickerSheetPage } from "./admin/StickerSheetPage";
import { TimesheetsPage } from "./admin/TimesheetsPage";
import { TodayPage } from "./admin/TodayPage";
import { VisitPage } from "./admin/VisitPage";
import { ClockPage } from "./cleaner/ClockPage";
import { HistoryPage } from "./cleaner/HistoryPage";
import { useSessionRefresh } from "./cleaner/hooks";
import { ScannedLinkPage } from "./cleaner/ScannedLinkPage";
import { CleanerSettingsPage } from "./cleaner/SettingsPage";
import { BrandImage } from "./components/Brand";
import { ErrorBanner, Loading } from "./components/ui";
import { SignIn } from "./SignIn";
import { SessionProvider, useSession } from "./session";

/** Once signed in: reloads after offline punches flush, polls while approval is pending, re-registers push. */
function SessionRefresh() {
  useSessionRefresh();
  return null;
}

function Gate() {
  const { me, loading, signedOut, error, reload } = useSession();
  if (loading && !me) return <Loading />;
  if (signedOut) return <SignIn onSignedIn={reload} />;
  if (!me) {
    return (
      <div className="page-narrow">
        <ErrorBanner error={error} onRetry={reload} />
      </div>
    );
  }
  return (
    <>
      <SessionRefresh />
      <Outlet />
    </>
  );
}

/** NKLEAN logo with a small label for which part of the app this is. */
function BrandLogo({ label, to }: { label: string; to: string }) {
  return (
    <Link to={to} className="brand-logo" aria-label={`NKLEAN ${label}`}>
      <BrandImage height={26} />
      <span className="app-label">{label}</span>
    </Link>
  );
}

function CleanerLayout() {
  const { me, offline } = useSession();
  const { pathname } = useLocation();
  return (
    <>
      <header className="topbar no-print">
        <div className="topbar-inner">
          <BrandLogo label="Hub" to="/" />
          <nav className="nav" style={{ marginLeft: "auto" }}>
            <NavLink to="/" end>
              Clock
            </NavLink>
            <NavLink to="/history">History</NavLink>
            <NavLink to="/settings">Settings</NavLink>
            {me?.user.role === "admin" && <NavLink to="/admin">Admin</NavLink>}
          </nav>
        </div>
      </header>
      {offline && pathname !== "/" && (
        <div className="offline-strip" role="status">
          No signal — showing saved details. Clock-ins are kept on this phone and sent when you're back
          online.
        </div>
      )}
      <main>
        <Outlet />
      </main>
    </>
  );
}

function AdminLayout() {
  const { me } = useSession();
  if (me?.user.role !== "admin") return <Navigate to="/" replace />;
  return (
    <>
      <header className="topbar no-print">
        <div className="topbar-inner">
          <BrandLogo label="Admin" to="/admin" />
          <NavLink to="/" className="btn ghost sm" style={{ marginLeft: "auto" }}>
            My clock
          </NavLink>
        </div>
        <div className="subnav">
          <nav className="nav">
            <NavLink to="/admin" end>
              Today
            </NavLink>
            <NavLink to="/admin/timesheets">Timesheets</NavLink>
            <NavLink to="/admin/sites">Sites</NavLink>
            <NavLink to="/admin/people">Cleaners</NavLink>
            <NavLink to="/admin/schedules">Schedules</NavLink>
            <NavLink to="/admin/exports">Exports</NavLink>
            <NavLink to="/admin/settings">Settings</NavLink>
            <NavLink to="/admin/audit">Audit log</NavLink>
          </nav>
        </div>
      </header>
      <main>
        <Outlet />
      </main>
    </>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          {/* A sticker scanned with the phone's own camera lands here. */}
          <Route path="/c/:token" element={<ScannedLinkPage />} />
          <Route element={<Gate />}>
            <Route element={<CleanerLayout />}>
              <Route index element={<ClockPage />} />
              <Route path="history" element={<HistoryPage />} />
              <Route path="settings" element={<CleanerSettingsPage />} />
            </Route>
            <Route path="admin/sites/print" element={<StickerSheetPage />} />
            <Route path="admin" element={<AdminLayout />}>
              <Route index element={<TodayPage />} />
              <Route path="timesheets" element={<TimesheetsPage />} />
              <Route path="visits/:id" element={<VisitPage />} />
              <Route path="sites" element={<SitesPage />} />
              <Route path="sites/:id" element={<SitePage />} />
              <Route path="people" element={<PeoplePage />} />
              <Route path="people/:id" element={<PersonPage />} />
              <Route path="schedules" element={<SchedulesPage />} />
              <Route path="exports" element={<ExportsPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
              <Route path="audit" element={<AuditPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}
